import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { apiGetRecentPpvs } from '../../api/ppvApi';
import type { RecentPpv } from '../../api/ppvApi';

const toSrc = (path: string | null | undefined): string | null => {
  if (!path) return null;
  return path.startsWith('http') ? path : (process.env.REACT_APP_STORAGE_URL ?? '') + path;
};

const formatDate = (dateStr: string): string =>
  new Date(dateStr).toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' });

// ─── Layout ───────────────────────────────────────────────────────────────────

const Section = styled.section<{ $isMobile: boolean }>`
  width: ${({ $isMobile }) => $isMobile ? '100%' : '80%'};
  margin: 0 0 ${({ $isMobile }) => $isMobile ? '28px' : '44px'};
`;

const SectionHeading = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 22px;

  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: rgba(255, 255, 255, 0.1);
  }
`;

const SectionTitle = styled.h2`
  margin: 0;
  font-size: 11px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.35);
  text-transform: uppercase;
  letter-spacing: 0.16em;
  white-space: nowrap;
`;

const CardsGrid = styled.div<{ $isMobile: boolean }>`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${({ $isMobile }) => $isMobile ? '12px' : '20px'};
`;

// ─── Card ─────────────────────────────────────────────────────────────────────

const Card = styled.div<{ $isMobile?: boolean }>`
  position: relative;
  border-radius: 16px;
  overflow: hidden;
  cursor: pointer;
  height: ${({ $isMobile }) => $isMobile ? '200px' : '300px'};
  background-color: #111;
  transition: transform 0.25s ease, box-shadow 0.25s ease;

  &:hover {
    transform: translateY(-6px) scale(1.015);
    box-shadow: 0 24px 52px rgba(0, 0, 0, 0.7);
  }
`;

const PosterImg = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center top;
`;

const FallbackBg = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(160deg, #1a1a2e 0%, #0f0f1a 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 52px;
`;

const Gradient = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 0) 25%,
    rgba(0, 0, 0, 0.5) 55%,
    rgba(0, 0, 0, 0.95) 100%
  );
`;


const CardContent = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 20px 18px 20px;
  z-index: 2;
`;

const PpvName = styled.div`
  font-size: 20px;
  font-weight: 800;
  color: #fff;
  line-height: 1.2;
  margin-bottom: 6px;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.9);
`;

const PpvMeta = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
  line-height: 1.6;
`;

const NightBadge = styled.div`
  display: inline-flex;
  align-items: center;
  margin-top: 8px;
  font-size: 10px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.45);
  background: rgba(255, 255, 255, 0.08);
  border-radius: 4px;
  padding: 3px 8px;
  letter-spacing: 0.04em;
`;

const CardOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  &::after {
    content: '';
    width: 32px;
    height: 32px;
    border: 3px solid rgba(255, 215, 0, 0.25);
    border-top-color: gold;
    border-radius: 50%;
    animation: spin 0.7s linear infinite;
  }
`;

// ─── Skeleton ─────────────────────────────────────────────────────────────────

const SkeletonCard = styled.div<{ $isMobile?: boolean }>`
  border-radius: 16px;
  height: ${({ $isMobile }) => $isMobile ? '200px' : '300px'};

  @keyframes shimmer {
    0%   { background-position: -600px 0; }
    100% { background-position: 600px 0; }
  }

  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.05) 25%,
    rgba(255, 255, 255, 0.1) 50%,
    rgba(255, 255, 255, 0.05) 75%
  );
  background-size: 1200px 100%;
  animation: shimmer 1.4s infinite linear;
`;

// ─── PpvCard ──────────────────────────────────────────────────────────────────

const PpvCard: React.FC<{ ppv: RecentPpv; isMobile?: boolean }> = ({ ppv, isMobile }) => {
  const navigate = useNavigate();
  const [isNavigating, setIsNavigating] = useState(false);

  const posterSrc = toSrc(ppv.img_path) ?? toSrc(ppv.ppv_description.img_path);
  const nights = [ppv.night1, ppv.night2, ppv.night3].filter(Boolean);

  const handleClick = () => {
    setIsNavigating(true);
    navigate(`/ppv/specific/i_${ppv.id}_d_${ppv.ppv_description.name}`);
  };

  return (
    <Card $isMobile={isMobile} onClick={handleClick}>
      {isNavigating && <CardOverlay />}

      {posterSrc
        ? <PosterImg src={posterSrc} alt={ppv.ppv_description.name} />
        : <FallbackBg>📺</FallbackBg>}

      <Gradient />

      <CardContent>
        <PpvName>{ppv.ppv_description.name}</PpvName>
        <PpvMeta>
          {formatDate(ppv.date)}
          {ppv.arena && <><br />{ppv.arena.name}</>}
        </PpvMeta>
        {nights.length > 1 && (
          <NightBadge>{nights.length} notti</NightBadge>
        )}
      </CardContent>
    </Card>
  );
};

// ─── RecentPpvsSection ────────────────────────────────────────────────────────

const RecentPpvsSection: React.FC<{ isMobile?: boolean }> = ({ isMobile = false }) => {
  const [ppvs, setPpvs] = useState<RecentPpv[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    apiGetRecentPpvs(3)
      .then((res) => { if (isMounted) setPpvs(res?.data ?? []); })
      .catch(() => {})
      .finally(() => { if (isMounted) setIsLoading(false); });
    return () => { isMounted = false; };
  }, []);

  if (!isLoading && ppvs.length === 0) return null;

  return (
    <Section $isMobile={isMobile}>
      <SectionHeading>
        <SectionTitle>Ultimi PPV</SectionTitle>
      </SectionHeading>
      <CardsGrid $isMobile={isMobile}>
        {isLoading
          ? Array.from({ length: 3 }).map((_, i) => <SkeletonCard key={i} $isMobile={isMobile} />)
          : ppvs.map((ppv) => <PpvCard key={ppv.id} ppv={ppv} isMobile={isMobile} />)}
      </CardsGrid>
    </Section>
  );
};

export default RecentPpvsSection;
